/**
 * Chat window state: which conversations are open, and which are minimised.
 *
 * `activeChats` and `minimizedChats` both hold chat OBJECTS, and a chat must be in
 * exactly ONE of them. Every bug this reducer had came from breaking that invariant:
 *
 *  - OPEN_CHAT filtered `minimizedChats` with `id => id !== payload.id`, comparing an
 *    OBJECT to a string. Always true, so nothing was ever removed: opening a minimised
 *    chat left it BOTH open and minimised, showing a window and a bubble for the same
 *    conversation. (CLOSE_CHAT right below it used `c => c.id !== …` correctly, which is
 *    what gave the bug away.)
 *  - MINIMIZE_CHAT appended without checking, so a chat already in the list was added a
 *    second time — two bubbles, and closing one left the other.
 *  - MAXIMIZE_CHAT appended to `activeChats` without checking, so a chat stuck in both
 *    lists opened a duplicate window.
 *
 * The helpers below exist so no case has to remember the rule.
 */

/** Open windows allowed at once. Beyond this the OLDEST is minimised rather than left to
 *  overflow the screen — desktop windows are a fixed width and simply run off the edge. */
export const MAX_OPEN_WINDOWS = 3;

const withoutId = (list, id) => (list || []).filter(c => c && c.id !== id);
/** Add to the front, never twice. */
const prepend = (list, chat) => [chat, ...withoutId(list, chat.id)];

export const initialChatState = {
    activeChats: [],
    minimizedChats: [],
    /** chatId -> unread count. Kept HERE so a bubble can show a badge without the list
     *  being mounted; the server value in conversations/{id}/unread/{uid} stays the
     *  source of truth and is pushed in via SET_UNREAD. */
    unread: {},
    isMainChatOpen: false,
    currentUser: null,
    userStatus: 'offline',
    isMockMode: false,
    users: [],
    agents: [],
    allUsers: [] // Derived state
};

export const chatReducer = (state, action) => {
    switch (action.type) {
        case 'SET_CURRENT_USER':
            return { ...state, currentUser: action.payload };
        case 'SET_USER_STATUS':
            return { ...state, userStatus: action.payload };
        case 'SET_MOCK_MODE':
            return { ...state, isMockMode: action.payload };
        case 'SET_USERS':
            return {
                ...state,
                users: action.payload,
                allUsers: [...action.payload, ...state.agents]
            };
        case 'SET_AGENTS':
            return {
                ...state,
                agents: action.payload,
                allUsers: [...state.users, ...action.payload]
            };

        case 'OPEN_CHAT': {
            const chat = action.payload;
            if (!chat || !chat.id) return state;
            let active = prepend(state.activeChats, chat);
            let minimized = withoutId(state.minimizedChats, chat.id);
            // Too many windows: the oldest steps aside into a bubble instead of being
            // dropped — a conversation must never disappear because another was opened.
            while (active.length > MAX_OPEN_WINDOWS) {
                const oldest = active[active.length - 1];
                active = active.slice(0, -1);
                minimized = prepend(minimized, oldest);
            }
            const unread = { ...(state.unread || {}) };
            delete unread[chat.id];              // opening a chat is reading it
            return { ...state, activeChats: active, minimizedChats: minimized, unread };
        }

        case 'CLOSE_CHAT':
            return {
                ...state,
                activeChats: withoutId(state.activeChats, action.payload),
                minimizedChats: withoutId(state.minimizedChats, action.payload),
            };

        case 'MINIMIZE_CHAT': {
            // Take it from whichever list holds it, so a chat left in both by an older
            // build is repaired rather than duplicated.
            const chat = (state.activeChats || []).find(c => c.id === action.payload)
                || (state.minimizedChats || []).find(c => c.id === action.payload);
            if (!chat) return state;
            return {
                ...state,
                activeChats: withoutId(state.activeChats, action.payload),
                minimizedChats: prepend(state.minimizedChats, chat),
            };
        }

        case 'MAXIMIZE_CHAT': {
            const chat = (state.minimizedChats || []).find(c => c.id === action.payload)
                || (state.activeChats || []).find(c => c.id === action.payload);
            if (!chat) return state;
            let active = prepend(state.activeChats, chat);
            let minimized = withoutId(state.minimizedChats, action.payload);
            while (active.length > MAX_OPEN_WINDOWS) {
                const oldest = active[active.length - 1];
                active = active.slice(0, -1);
                minimized = prepend(minimized, oldest);
            }
            return { ...state, activeChats: active, minimizedChats: minimized };
        }

        /** Push the server's unread counts in. Replaces the map wholesale: a chat the
         *  server no longer reports is a chat with nothing unread. */
        case 'SET_UNREAD': {
            const next = {};
            for (const [id, n] of Object.entries(action.payload || {})) {
                const c = Number(n) || 0;
                if (c > 0) next[id] = c;          // only carry what is actually unread
            }
            return { ...state, unread: next };
        }

        /** Reading a conversation clears its badge immediately, without waiting for the
         *  server round trip — the badge must not linger on a chat the user is looking at. */
        case 'MARK_READ': {
            if (!state.unread || !state.unread[action.payload]) return state;
            const next = { ...state.unread };
            delete next[action.payload];
            return { ...state, unread: next };
        }

        /** Re-open the windows a previous page load had open. Never trusted blindly —
         *  dockPersistence validates, and the cap is applied here as for any open. */
        case 'RESTORE_DOCK': {
            const { activeChats = [], minimizedChats = [] } = action.payload || {};
            let active = [];
            for (const c of activeChats) if (c && c.id) active = [...withoutId(active, c.id), c];
            let minimized = [];
            const activeIds = new Set(active.map(c => c.id));
            for (const c of minimizedChats) {
                if (!c || !c.id || activeIds.has(c.id)) continue;
                minimized = [...withoutId(minimized, c.id), c];
            }
            while (active.length > MAX_OPEN_WINDOWS) {
                minimized = prepend(minimized, active[active.length - 1]);
                active = active.slice(0, -1);
            }
            return { ...state, activeChats: active, minimizedChats: minimized };
        }

        case 'SET_MAIN_CHAT_OPEN':
            return { ...state, isMainChatOpen: action.payload };
        default:
            return state;
    }
};
